import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';

export interface IntegrationSettings {
  financing_url: string | null;
  financing_enabled: boolean;
  financing_cta_text: string;
  booking_embed_code: string | null;
  chatbot_embed_code: string | null;
}

const defaultSettings: IntegrationSettings = {
  financing_url: null,
  financing_enabled: false,
  financing_cta_text: 'Apply for Financing',
  booking_embed_code: null,
  chatbot_embed_code: null,
};

export const useIntegrationSettings = () => {
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['integration-settings'],
    queryFn: async (): Promise<IntegrationSettings> => {
      const { data, error } = await pmsFrom('business_settings')
        .select('financing_url, financing_enabled, financing_cta_text, booking_embed_code, chatbot_embed_code')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching integration settings:', error);
        return defaultSettings;
      }

      return {
        financing_url: data?.financing_url ?? null,
        financing_enabled: data?.financing_enabled ?? false,
        financing_cta_text: data?.financing_cta_text ?? 'Apply for Financing',
        booking_embed_code: data?.booking_embed_code ?? null,
        chatbot_embed_code: data?.chatbot_embed_code ?? null,
      };
    },
    staleTime: 1000 * 60 * 5,
  });

  const updateSettings = useMutation({
    mutationFn: async (newSettings: Partial<IntegrationSettings>) => {
      const { data: existing } = await pmsFrom('business_settings')
        .select('id')
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (existing) {
        const { error } = await pmsFrom('business_settings')
          .update(newSettings)
          .eq('id', existing.id);
        if (error) throw error;
      } else {
        const { error } = await pmsFrom('business_settings')
          .insert({ company_name: 'Your Company', ...newSettings });
        if (error) throw error;
      }
      return newSettings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration-settings'] });
    },
  });

  return {
    settings: settings ?? defaultSettings,
    isLoading,
    updateSettings,
  };
};
